import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  FileText,
  Shield,
  Target,
  Building2,
  HelpCircle,
  Layers,
  Construction,
  Hammer,
  Mountain,
  Droplets,
  Wrench
} from 'lucide-react';

const HowToChooseSpecialFoundations = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce qu'une fondation spéciale ?" },
    { id: 'section-2', title: "Les principales techniques" },
    { id: 'section-3', title: "Tableau récapitulatif" },
    { id: 'section-4', title: "Les critères pour bien choisir" },
    { id: 'section-5', title: "Questions fréquentes" },
    { id: 'section-6', title: "Bien choisir, c'est aussi disposer du bon matériel" },
  ];

  const faqs = [
    {
      question: "Quelle différence entre un pieu et un micropieu ?",
      answer: "La différence principale est le diamètre : on parle de micropieu en dessous de 300 mm. Le micropieu travaille surtout au frottement latéral et excelle dans les sites exigus et la reprise en sous-œuvre, là où une machine de gros forage ne passerait pas."
    },
    {
      question: "Les fondations spéciales sont-elles couvertes par la garantie décennale ?",
      answer: "Oui. Dès lors qu'elles participent à la stabilité de la construction, les techniques de fondations profondes (pieux, micropieux, parois moulées, colonnes ballastées, jet-grouting, injections, reprises en sous-œuvre) relèvent de l'assurance décennale. Les études géotechniques seules, sans travaux, relèvent de la RC professionnelle."
    },
    {
      question: "Quelle technique pour réparer une maison fissurée ?",
      answer: "La reprise en sous-œuvre par micropieux est la solution la plus courante, parfois complétée par du jet-grouting. Elle consolide les fondations existantes en reportant les charges vers une couche stable. Un diagnostic géotechnique (mission G5) doit précéder ces travaux."
    },
    {
      question: "Faut-il une étude de sol avant des fondations spéciales ?",
      answer: "Oui, c'est indispensable. Le choix et le dimensionnement d'une fondation spéciale reposent sur le modèle de sol fourni par l'étude géotechnique de conception (G2), puis affiné en phase d'exécution (G3)."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Comment choisir ses fondations spéciales : guide des techniques (pieux, micropieux, parois moulées)",
    "description": "Pieux, micropieux, parois moulées, colonnes ballastées, jet-grouting : quelle fondation spéciale choisir selon votre sol et votre projet ? Le guide complet.",
    "image": "https://geots.consulting/og-images/guide-fondations-speciales.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-06-29",
    "dateModified": "2026-06-29"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Techniques data
  const techniques = [
    {
      name: "Pieux forés",
      icon: Construction,
      color: "blue",
      description: "C'est la technique de fondation profonde la plus courante. Un forage est réalisé à la tarière, un tubage est mis en place si nécessaire pour tenir les parois, puis on installe les armatures avant de bétonner. Les pieux forés permettent classiquement des diamètres de 300 à 1200 mm, à des profondeurs pouvant atteindre une trentaine de mètres.",
      advantages: "Grande capacité portante, adaptés aux ouvrages lourds (bâtiments, ponts, ouvrages d'art)."
    },
    {
      name: "Pieux battus (métalliques)",
      icon: Hammer,
      color: "gray",
      description: "Le pieu métallique battu est constitué de profilés (type H ou tubulaires) directement enfoncés dans le sol à l'aide d'un mouton. Plusieurs éléments peuvent être soudés bout à bout selon la profondeur visée.",
      advantages: "Mise en œuvre rapide, bien adapté aux sols pulvérulents. Souvent utilisés aussi pour réaliser des parois berlinoises."
    },
    {
      name: "Micropieux",
      icon: Target,
      color: "green",
      description: "Le micropieu est la fondation spéciale « tout terrain ». Ce sont des pieux forés de diamètre inférieur à 300 mm, travaillant essentiellement au frottement latéral. Le forage est équipé d'une armature métallique et rempli de coulis de ciment. Le micropieu peut être foré à travers tous types de matériaux (blocs, sols lâches, substratum rocheux) et reprendre aussi bien des charges de compression que de traction.",
      advantages: "Idéal pour les zones difficiles d'accès, les sites exigus et surtout la reprise en sous-œuvre de bâtiments existants ou fissurés. C'est la technique de référence pour conforter une construction sinistrée."
    },
    {
      name: "Parois moulées",
      icon: Layers,
      color: "purple",
      description: "La paroi moulée est un mur en béton armé coulé directement dans le sol, dans une tranchée maintenue par une boue stabilisatrice. Elle assure à la fois le soutènement des terres et l'étanchéité, et peut descendre très profond.",
      advantages: "Indispensable pour les sous-sols profonds, parkings enterrés, stations de métro, bassins. Très performante mais coûteuse à installer."
    },
    {
      name: "Colonnes ballastées",
      icon: Mountain,
      color: "orange",
      description: "Il s'agit d'une technique d'amélioration de sol. Dans les sols cohérents (limons, argiles), un vibreur crée une empreinte verticale ensuite comblée par du ballast compacté. Les colonnes ainsi formées améliorent les performances globales du sol et reportent les charges vers une couche plus résistante.",
      advantages: "Économique pour traiter de grandes surfaces sur sols compressibles, sans incorporation de liant."
    },
    {
      name: "Jet-grouting",
      icon: Droplets,
      color: "cyan",
      description: "Le jet-grouting consiste à injecter un liant (coulis de ciment) sous très haute pression dans un forage, ce qui crée une colonne de sol stabilisée (mélange sol-ciment). C'est une technique d'amélioration de sol particulièrement adaptée aux sites exigus.",
      advantages: "Alternative au coût élevé d'une paroi moulée dans les contextes délicats (mitoyens, ouvrages souterrains existants). Très utilisé en reprise en sous-œuvre et traitement de terrain."
    },
    {
      name: "Injections de sol",
      icon: Shield,
      color: "yellow",
      description: "Les travaux d'injection consistent à introduire un coulis dans le sol pour l'étanchéifier ou le renforcer. Souvent associés au jet-grouting ou aux parois, ils traitent les terrains perméables et améliorent leur cohésion.",
      advantages: "Traitement des terrains perméables, étanchéification, amélioration de la cohésion du sol."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Comment choisir ses fondations spéciales : guide des techniques | GEOTS</title>
        <meta name="description" content="Pieux, micropieux, parois moulées, colonnes ballastées, jet-grouting : quelle fondation spéciale choisir selon votre sol et votre projet ? Le guide complet." />
        <link rel="canonical" href="https://geots.consulting/guides/comment-choisir-fondations-speciales" />
        <meta property="og:title" content="Comment choisir ses fondations spéciales : guide des techniques" />
        <meta property="og:description" content="Pieux, micropieux, parois moulées, colonnes ballastées, jet-grouting : quelle fondation spéciale choisir selon votre sol et votre projet ?" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/comment-choisir-fondations-speciales" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Fondations spéciales</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE COMPLET
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 29 juin 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  12 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Comment choisir ses fondations spéciales :
                <span className="text-[#00D4FF]"> le guide complet des techniques</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Votre projet repose sur un sol difficile : terrain meuble, charges lourdes, nappe phréatique, 
                site exigu en milieu urbain, ou bâtiment existant à conforter ? Les fondations classiques ne suffisent pas. 
                Place aux fondations spéciales. Mais entre pieux, micropieux, parois moulées, colonnes ballastées 
                ou jet-grouting, quelle technique choisir ? Ce guide vous aide à y voir clair.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/travaux-fondations" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander un devis travaux
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/annuaire/travaux-fondations-speciales" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Trouver une entreprise
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Links to related guides */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-xs text-gray-500 mb-2">Guides connexes</p>
                  <Link 
                    to="/guides/comment-choisir-foreuse-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1 mb-2"
                  >
                    <Wrench className="w-4 h-4" />
                    Choisir une foreuse
                  </Link>
                  <Link 
                    to="/guides/comment-choisir-etude-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    Choisir son étude de sol
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Qu'est-ce qu'une fondation spéciale ?
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    On parle de <strong>fondations spéciales</strong> (ou fondations profondes) lorsque les fondations 
                    superficielles — semelles, radiers — ne peuvent pas reprendre les charges de l'ouvrage en toute sécurité. 
                    C'est le cas lorsque le sol d'assise est trop compressible en surface, que les charges sont importantes, 
                    ou que la couche résistante se situe en profondeur.
                  </p>

                  <p className="text-gray-700 mb-6">
                    Le principe : <strong>reporter les charges vers une couche de sol plus profonde et plus résistante</strong>, 
                    ou bien <strong>améliorer les caractéristiques du sol en place</strong>. Ces ouvrages participent directement 
                    à la stabilité de la construction, ce qui les place sous garantie décennale.
                  </p>

                  <p className="text-gray-700 mb-8">
                    Les fondations spéciales recouvrent un large éventail de techniques. On peut les regrouper en 
                    <strong> trois grandes familles</strong> :
                  </p>

                  <div className="grid md:grid-cols-1 gap-4 mb-8">
                    {[
                      { icon: Construction, title: "Fondations profondes", desc: "Pieux forés, pieux battus, micropieux", color: "blue" },
                      { icon: Layers, title: "Soutènement et étanchéité", desc: "Parois moulées, parois berlinoises", color: "purple" },
                      { icon: Mountain, title: "Amélioration de sol", desc: "Colonnes ballastées, jet-grouting, injections", color: "orange" },
                    ].map((item, i) => (
                      <div key={i} className="bg-white p-4 rounded-lg border flex items-start gap-3">
                        <div className={`bg-${item.color}-100 p-2 rounded-lg`}>
                          <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0A2540]">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 2 - Techniques */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Les principales techniques de fondations spéciales
                  </h2>

                  {techniques.map((technique, i) => (
                    <div key={i} className={`bg-white rounded-xl border-l-4 ${
                      technique.color === 'blue' ? 'border-l-blue-500' :
                      technique.color === 'green' ? 'border-l-green-500' :
                      technique.color === 'purple' ? 'border-l-purple-500' :
                      technique.color === 'orange' ? 'border-l-orange-500' :
                      technique.color === 'cyan' ? 'border-l-cyan-500' :
                      technique.color === 'yellow' ? 'border-l-yellow-500' :
                      'border-l-gray-500'
                    } shadow-sm p-6 mb-6`}>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                        <technique.icon className={`w-6 h-6 ${
                          technique.color === 'blue' ? 'text-blue-500' :
                          technique.color === 'green' ? 'text-green-500' :
                          technique.color === 'purple' ? 'text-purple-500' :
                          technique.color === 'orange' ? 'text-orange-500' :
                          technique.color === 'cyan' ? 'text-cyan-500' :
                          technique.color === 'yellow' ? 'text-yellow-500' :
                          'text-gray-500'
                        }`} />
                        {technique.name}
                      </h3>
                      <p className="text-gray-700 mb-4">{technique.description}</p>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800"><strong>Atouts :</strong> {technique.advantages}</p>
                      </div>
                    </div>
                  ))}
                </section>

                {/* Section 3 - Tableau récapitulatif */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    Tableau récapitulatif : quelle technique pour quel besoin ?
                  </h2>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Votre besoin</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Technique adaptée</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[
                          ["Reporter de fortes charges vers une couche profonde", "Pieux forés / pieux battus"],
                          ["Site exigu, accès difficile, reprise en sous-œuvre", "Micropieux"],
                          ["Bâtiment fissuré à conforter", "Micropieux, jet-grouting"],
                          ["Sous-sol profond, soutènement + étanchéité", "Paroi moulée"],
                          ["Améliorer un sol compressible sur grande surface", "Colonnes ballastées"],
                          ["Améliorer le sol en site contraint (mitoyens)", "Jet-grouting"],
                          ["Étanchéifier ou renforcer un terrain perméable", "Injections de sol"],
                        ].map(([besoin, technique], i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">{besoin}</td>
                            <td className="px-4 py-3">
                              <span className="bg-[#635BFF]/10 text-[#635BFF] px-3 py-1 rounded-full text-sm font-medium">
                                {technique}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 4 - Critères */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Les critères pour bien choisir
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Le choix d'une technique de fondation spéciale ne se fait jamais au hasard. 
                    Il dépend de plusieurs facteurs croisés :
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      { title: "La nature du sol", desc: "C'est l'étude géotechnique (mission G2) qui fournit le modèle de sol et oriente le choix. Aucune décision sérieuse sans elle." },
                      { title: "Les charges à reprendre", desc: "Poids et type de l'ouvrage, efforts de compression et de traction." },
                      { title: "La profondeur de la couche d'ancrage", desc: "Profondeur à laquelle se trouve la couche résistante." },
                      { title: "Les contraintes du site", desc: "Accès, espace disponible, présence de mitoyens, milieu urbain dense." },
                      { title: "Les nuisances admissibles", desc: "Bruit, vibrations (certaines techniques comme le forage à la tarière en génèrent peu)." },
                      { title: "Le budget", desc: "Une paroi moulée et un réseau de micropieux n'ont pas le même coût." },
                    ].map((criterion, i) => (
                      <div key={i} className="bg-white rounded-lg border p-4 flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#635BFF] flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-[#0A2540]">{criterion.title}</h4>
                          <p className="text-sm text-gray-600">{criterion.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Point clé :</strong> le bon choix résulte du dialogue entre le <strong>bureau d'études 
                      géotechniques</strong> (qui caractérise le sol et dimensionne) et l'<strong>entreprise de 
                      fondations spéciales</strong> (qui met en œuvre). Les deux compétences sont complémentaires.
                    </p>
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="inline-flex items-center gap-2 text-[#635BFF] font-semibold mt-3 hover:underline"
                    >
                      <FileText className="w-4 h-4" />
                      Lire le guide : Comment choisir son étude géotechnique
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </section>

                {/* Section 5 - FAQ */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 6 - Conclusion */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Bien choisir, c'est aussi disposer du bon matériel
                  </h2>

                  <p className="text-gray-700 mb-6">
                    La réalisation de fondations spéciales mobilise un matériel spécifique : foreuses, ateliers 
                    d'injection, instrumentation de contrôle. La qualité et la fiabilité de cet équipement 
                    conditionnent directement la performance et la sécurité de l'ouvrage.
                  </p>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6 mb-8">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Besoin de matériel de forage ?</strong> Consultez notre guide complet sur le choix 
                      des foreuses géotechniques pour réaliser vos sondages, micropieux et fondations profondes.
                    </p>
                    <Link 
                      to="/guides/comment-choisir-foreuse-geotechnique"
                      className="inline-flex items-center gap-2 text-[#635BFF] font-semibold mt-3 hover:underline"
                    >
                      <Wrench className="w-4 h-4" />
                      Lire le guide : Comment choisir une foreuse géotechnique
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">Comment GEOTS Consulting peut vous aider</h2>
                  <p className="mb-6 text-white/90">
                    GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : 
                    entreprises de travaux, bureaux d'études, loueurs de matériel et prestataires spécialisés. 
                    Un projet de fondations spéciales à cadrer, du matériel à louer ou un prestataire à trouver ?
                  </p>
                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <p className="font-semibold">Offre de lancement</p>
                    <p className="text-white/90">
                      Les 100 premiers fournisseurs bénéficient de 6 mois Premium offerts. Sans engagement, sans carte bancaire.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/travaux-fondations" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Demander un devis
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/annuaire/travaux-fondations-speciales" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors">
                      Trouver une entreprise
                    </Link>
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link 
                      to="/guides/paroi-moulee-technique-realisation"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Paroi moulée : technique et réalisation</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-foreuse-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir une foreuse géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir son étude géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif. Le choix d'une technique de fondation spéciale 
                    doit toujours s'appuyer sur une étude géotechnique et l'avis d'un bureau d'études 
                    et d'une entreprise qualifiés.
                  </p>
                  <p className="mt-2">Dernière mise à jour : 29 juin 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowToChooseSpecialFoundations;
